import { useEffect, useRef } from 'react'
import {
  vertexShader,
  fragmentShader,
  defaultUniforms,
  BlendMode,
  blendModeToInt,
} from './dotsGrid'

type UniformValue = number | [number, number] | [number, number, number]

export interface ShaderConfig {
  u_timeScale?: number
  u_lineSpacing?: number
  u_dotsPerCell?: number
  u_dotSize?: number
  u_dotPaddingBoost?: number
  u_lineThickness?: number
  u_gradientTop?: number
  // Background gradient
  gradientColorTop?: string // hex color e.g. "#ff0000"
  gradientColorBottom?: string // hex color
  gradientAngle?: number // degrees, 90 = bottom to top
  blendMode?: BlendMode
  gridColor?: string // hex color for the grid, default white
}

export interface ShaderBackgroundProps {
  zIndex?: number
  shaderConfig?: ShaderConfig
}

// Convert shader angle to CSS gradient angle
// Shader: 0° = right, 90° = up (standard trig)
// CSS: 0° = up, 90° = right
function shaderAngleToCssAngle(shaderAngle: number): number {
  return 90 - shaderAngle
}

function createShader(
  gl: WebGL2RenderingContext,
  type: number,
  source: string
): WebGLShader | null {
  const shader = gl.createShader(type)
  if (!shader) return null
  gl.shaderSource(shader, source)
  gl.compileShader(shader)
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    console.error('Shader compile error:', gl.getShaderInfoLog(shader))
    gl.deleteShader(shader)
    return null
  }
  return shader
}

function createProgram(
  gl: WebGL2RenderingContext,
  vertShader: WebGLShader,
  fragShader: WebGLShader
): WebGLProgram | null {
  const program = gl.createProgram()
  if (!program) return null
  gl.attachShader(program, vertShader)
  gl.attachShader(program, fragShader)
  gl.linkProgram(program)
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    console.error('Program link error:', gl.getProgramInfoLog(program))
    gl.deleteProgram(program)
    return null
  }
  return program
}

export default function ShaderBackground({
  zIndex,
  shaderConfig,
}: ShaderBackgroundProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const shaderConfigRef = useRef(shaderConfig)

  // Keep shaderConfig ref up to date
  useEffect(() => {
    shaderConfigRef.current = shaderConfig
  }, [shaderConfig])

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const gl = canvas.getContext('webgl2', {
      alpha: true,
      premultipliedAlpha: true,
      antialias: true,
    })
    if (!gl) {
      console.error('WebGL2 not supported')
      return
    }

    // Compile shaders
    const vert = createShader(gl, gl.VERTEX_SHADER, vertexShader)
    const frag = createShader(gl, gl.FRAGMENT_SHADER, fragmentShader)
    if (!vert || !frag) return

    const program = createProgram(gl, vert, frag)
    if (!program) return
    gl.useProgram(program)

    // Set up fullscreen quad
    const positions = new Float32Array([
      -1, -1, 0, 0, 0, 1, -1, 0, 1, 0, -1, 1, 0, 0, 1, 1, 1, 0, 1, 1,
    ])
    const vao = gl.createVertexArray()
    gl.bindVertexArray(vao)

    const buffer = gl.createBuffer()
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer)
    gl.bufferData(gl.ARRAY_BUFFER, positions, gl.STATIC_DRAW)

    const posLoc = gl.getAttribLocation(program, 'position')
    gl.enableVertexAttribArray(posLoc)
    gl.vertexAttribPointer(posLoc, 3, gl.FLOAT, false, 20, 0)

    const uvLoc = gl.getAttribLocation(program, 'uv')
    gl.enableVertexAttribArray(uvLoc)
    gl.vertexAttribPointer(uvLoc, 2, gl.FLOAT, false, 20, 12)

    // Enable blending for transparency
    gl.enable(gl.BLEND)
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA)

    // Cache uniform locations
    const uniformLocations = new Map<string, WebGLUniformLocation | null>()
    const getUniformLoc = (name: string) => {
      if (!uniformLocations.has(name)) {
        uniformLocations.set(name, gl.getUniformLocation(program, name))
      }
      return uniformLocations.get(name)
    }

    const setUniform = (
      name: string,
      value: UniformValue,
      forceInt = false
    ) => {
      const loc = getUniformLoc(name)
      if (!loc) return
      if (Array.isArray(value)) {
        if (value.length === 3) {
          gl.uniform3f(loc, value[0], value[1], value[2])
        } else {
          gl.uniform2f(loc, value[0], value[1])
        }
      } else if (forceInt) {
        gl.uniform1i(loc, value)
      } else {
        gl.uniform1f(loc, value)
      }
    }

    // Helper to convert hex color to RGB array [0-1]
    const hexToRgb = (hex: string): [number, number, number] => {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
      if (result) {
        return [
          parseInt(result[1], 16) / 255,
          parseInt(result[2], 16) / 255,
          parseInt(result[3], 16) / 255,
        ]
      }
      return [0, 0, 0]
    }

    // Handle resize
    const handleResize = () => {
      const dpr = window.devicePixelRatio || 1
      const rect = canvas.getBoundingClientRect()
      canvas.width = rect.width * dpr
      canvas.height = rect.height * dpr
      gl.viewport(0, 0, canvas.width, canvas.height)
    }

    const resizeObserver = new ResizeObserver(handleResize)
    resizeObserver.observe(canvas)
    handleResize()

    const startTime = performance.now()
    let animationId = 0

    // Animation loop
    const render = () => {
      const dpr = window.devicePixelRatio || 1
      const time = (performance.now() - startTime) / 1000

      gl.clearColor(0, 0, 0, 0)
      gl.clear(gl.COLOR_BUFFER_BIT)

      // Set built-in uniforms
      setUniform('u_resolution', [canvas.width, canvas.height])
      setUniform('u_time', time)
      setUniform('u_dpr', dpr)
      setUniform('u_mouse', [0, 0])

      // Apply default uniforms
      for (const [key, val] of Object.entries(defaultUniforms)) {
        if (!['u_resolution', 'u_time', 'u_dpr', 'u_mouse'].includes(key)) {
          const isInt = key === 'u_blendMode'
          setUniform(key, val.value as UniformValue, isInt)
        }
      }

      // Apply config overrides
      const config = shaderConfigRef.current
      if (config) {
        for (const [key, val] of Object.entries(config)) {
          if (val === undefined) continue
          // Handle special config keys
          if (key === 'gradientColorTop') {
            setUniform('u_gradientColorTop', hexToRgb(val as string))
          } else if (key === 'gradientColorBottom') {
            setUniform('u_gradientColorBottom', hexToRgb(val as string))
          } else if (key === 'gradientAngle') {
            setUniform('u_gradientAngle', val as number)
          } else if (key === 'blendMode') {
            setUniform('u_blendMode', blendModeToInt[val as BlendMode], true)
          } else if (key === 'gridColor') {
            setUniform('u_gridColor', hexToRgb(val as string))
          } else if (key.startsWith('u_')) {
            setUniform(key, val as number)
          }
        }
      }

      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4)
      animationId = requestAnimationFrame(render)
    }

    render()

    return () => {
      cancelAnimationFrame(animationId)
      resizeObserver.disconnect()
      gl.deleteProgram(program)
      gl.deleteShader(vert)
      gl.deleteShader(frag)
      gl.deleteBuffer(buffer)
      gl.deleteVertexArray(vao)
    }
  }, [])

  // Build CSS background gradient to prevent white flash during resize
  const cssBackground =
    shaderConfig?.gradientColorTop && shaderConfig?.gradientColorBottom
      ? `linear-gradient(${shaderAngleToCssAngle(shaderConfig.gradientAngle ?? 90)}deg, ${shaderConfig.gradientColorBottom}, ${shaderConfig.gradientColorTop})`
      : undefined

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: zIndex,
        background: cssBackground,
      }}
    />
  )
}
